// Provides react-hook-form instance to all form components
// Handles form state, validation, and save orchestration
import { createContext, useContext, useMemo, useCallback, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { ReactNode } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { SectionConfig } from '../types/sections';

// Merged schema from all sections
function buildFormSchema(sections: SectionConfig[]): z.ZodSchema {
  const schemas = sections.reduce(
    (acc, section) => ({
      ...acc,
      ...((section.validationSchema as z.ZodObject<z.ZodRawShape>).shape || {}),
    }),
    {} as z.ZodRawShape
  );
  return z.object(schemas);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type StartupFormData = Record<string, any>;

export interface StartupFormContextValue {
  form: UseFormReturn<StartupFormData>;
  isDirty: boolean;
  isSubmitting: boolean;
  save: () => Promise<boolean>; // returns success
  getSavedValues: () => StartupFormData;
  discardChanges: (fieldNames?: string[]) => void; // reset to last saved values
  // Called when a conditional field is hidden — deletes its BE value if one exists
  onFieldHidden?: (fieldName: string, valueId: string) => Promise<void>;
}

const StartupFormContext = createContext<StartupFormContextValue | null>(null);

export interface StartupFormProviderProps {
  sections: SectionConfig[];
  defaultValues?: Partial<StartupFormData>;
  onSave: (data: StartupFormData) => Promise<void>;
  onFieldHidden?: (fieldName: string, valueId: string) => Promise<void>;
  children: ReactNode;
}

export function StartupFormProvider({
  sections,
  defaultValues = {},
  onSave,
  onFieldHidden,
  children,
}: StartupFormProviderProps) {
  // Build merged schema from all sections
  const schema = useMemo(() => buildFormSchema(sections), [sections]);

  const form = useForm<StartupFormData>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur', // validate on blur, not on every change
  });

  // Track last saved values (starts with defaultValues)
  const savedValuesRef = useRef<StartupFormData>({ ...defaultValues });

  const save = useCallback(async (): Promise<boolean> => {
    const isValid = await form.trigger();
    if (!isValid) return false;

    try {
      const currentValues = form.getValues();
      await onSave(currentValues);
      // Update saved values after successful save
      savedValuesRef.current = { ...currentValues };
      // Reset form state to mark as not dirty
      form.reset(currentValues);
      return true;
    } catch {
      return false;
    }
  }, [form, onSave]);

  const getSavedValues = useCallback(() => savedValuesRef.current, []);

  // Reset specific fields (or all) to last saved values.
  // Baseline is react-hook-form's own live defaultValues, not savedValuesRef: savedValuesRef is
  // only ever updated by the legacy save() path below (StartupFormFlow's onboarding wizard) and
  // stays frozen at page-load values for callers using onSaveSection (the profile edit page) or
  // resetField (hero identity/stage auto-commits), both of which already keep
  // form.formState.defaultValues current instead. Reading the stale ref there made "Descartar
  // cambios" silently revert an already-saved field back to its pre-edit value, contradicted by
  // a refresh showing the save had gone through (OPA-343).
  const discardChanges = useCallback(
    (fieldNames?: string[]) => {
      const saved = (form.formState.defaultValues as StartupFormData) ?? savedValuesRef.current;
      if (fieldNames) {
        // Reset only specified fields
        fieldNames.forEach((name) => {
          form.setValue(name, saved[name], { shouldDirty: false });
        });
        // Reset dirty state for these fields
        form.reset(form.getValues(), { keepValues: true });
      } else {
        // Reset entire form
        form.reset(saved);
      }
    },
    [form]
  );

  const value = useMemo<StartupFormContextValue>(
    () => ({
      form,
      isDirty: form.formState.isDirty,
      isSubmitting: form.formState.isSubmitting,
      save,
      getSavedValues,
      discardChanges,
      onFieldHidden,
    }),
    [
      form,
      form.formState.isDirty,
      form.formState.isSubmitting,
      save,
      getSavedValues,
      discardChanges,
      onFieldHidden,
    ]
  );

  return (
    <StartupFormContext.Provider value={value}>
      <FormProvider {...form}>{children}</FormProvider>
    </StartupFormContext.Provider>
  );
}

// Hook to access form context
export function useStartupForm(): StartupFormContextValue {
  const ctx = useContext(StartupFormContext);
  if (!ctx) {
    throw new Error('useStartupForm must be used within StartupFormProvider');
  }
  return ctx;
}
